import { Cabinet } from 'lib/modelBuilder/entities/Cabinet';
import { Shelf } from 'lib/modelBuilder/entities/Shelf';
import { put, select } from '@redux-saga/core/effects';
import {
  toggleSelectionDoorAction,
  toggleSelectionPaneAction,
} from 'containers/Ribbon/ShelvesRibbon/actions';
import { ShelfSelection } from 'lib/modelBuilder/entities/ShelfSelection';
import { makeSelectShelfSelectionProfile } from 'containers/Ribbon/ShelvesRibbon/selectors';
import { makeSelectIsVisibleDrawersCompositionPanel } from 'containers/DrawersCompositionPanel/selectors';
import { displayDrawersCompositionPanelAction } from 'containers/DrawersCompositionPanel/actions';
import {
  GLASS_COLOR,
  MINIMUM_LARGE_DOORS_HEIGHT,
  PaneMaterial,
  Position,
} from 'lib/modelBuilder/constants';
import { Plate } from 'lib/modelBuilder/pieces/Plate';
import {
  makeSelectCounterSelectedDrawersInShelvesSelected,
  makeSelectDrawersSelected,
} from 'containers/DrawersPanel/selectors';
import { findColor } from 'lib/modelBuilder/utils/colorHelper';
import { LevelSelection } from 'containers/DrawersPanel/types';
import { selectionDrawersAction } from 'containers/DrawersPanel/actions';
import _ from 'lodash';
import { Rack } from '../../../../lib/modelBuilder/entities/Rack';
import { makeSelectHandlesSelection } from '../../../HandlesPanel/selectors';
import { HandleUserSelection } from '../../../HandlesPanel/types';
import { makeSelectIsVisibleColorsPanel } from '../../selectors/general';
import { ActivePanel } from '../../constants/general';

export function* postSuccessaddPieceSaga(cabinetNext: Cabinet, payload: any) {
  const { type, position } = payload;
  if (type === 'Door') {
    yield put(toggleSelectionDoorAction(position));
  }
  if (type === 'Pane') {
    yield put(toggleSelectionPaneAction(position));
  }
}

export function* addPieceSaga(
  cabinetOrigin: Cabinet,
  cabinetNext: Cabinet,
  payload: any,
  selectedShelves: Shelf[],
) {
  const { type, position } = payload;

  for (const shelf of selectedShelves) {
    if (type === 'Door') {
      shelf.addDoor(position);
    }
    if (type === 'Pane') {
      shelf.addPane(position);
    }
  }
  return {
    ignore: false,
    postSuccess: postSuccessaddPieceSaga,
  };
}

export function* removePieceSaga(
  cabinetOrigin: Cabinet,
  cabinetNext: Cabinet,
  payload: any,
  selectedShelves: Shelf[],
) {
  const shelfSelectionProfile: ShelfSelection = yield select(
    makeSelectShelfSelectionProfile(),
  );

  for (const shelf of selectedShelves) {
    for (const position of shelfSelectionProfile.doors)
      shelf.removeDoor(position);

    for (const position of shelfSelectionProfile.panes) {
      for (const rack of shelf.racks) {
        rack.removePane(position);
      }
    }
  }

  return {
    ignore: false,
    postSuccess: null,
  };
}

export function* heightShelfsSaga(
  cabinetOrigin: Cabinet,
  cabinetNext: Cabinet,
  payload: any,
  selectedShelves: Shelf[],
) {
  const { height } = payload;
  for (const shelf of selectedShelves) {
    shelf.height = height;
  }

  return {
    ignore: false,
    postSuccess: null,
  };
}

export function* postSuccessDrawersSaga(cabinetNext: Cabinet, payload: any) {
  /**
   * Update the selection of drawers because we could have less drawers than the previous selection
   * If user change the composition we selected
   * Close the combination drawers panel after selection for UX
   */
  const { preview } = payload;
  if (!preview) {
    const isVisiblePanel = yield select(
      makeSelectIsVisibleDrawersCompositionPanel(),
    );
    const drawersSelection: LevelSelection = yield select(
      makeSelectDrawersSelected(),
    );
    const numberDrawers = yield select(
      makeSelectCounterSelectedDrawersInShelvesSelected(),
    );

    if (isVisiblePanel) yield put(displayDrawersCompositionPanelAction(false));
    if (drawersSelection.length > 0) {
      if (drawersSelection === 'All') {
        if (numberDrawers === 1) {
          yield put(selectionDrawersAction([0]));
        }
      } else {
        yield put(selectionDrawersAction(_.range(0, numberDrawers)));
      }
    }
  }
}

export function* drawersSaga(
  cabinetOrigin: Cabinet,
  cabinetNext: Cabinet,
  payload: any,
  selectedShelves: Shelf[],
) {
  const { combos } = payload;

  for (const shelf of selectedShelves) {
    shelf.setDrawersCombination(combos);
  }
  return {
    ignore: false,
    postSuccess: postSuccessDrawersSaga,
  };
}

export function* drawersUpdateSaga(
  cabinetOrigin: Cabinet,
  cabinetNext: Cabinet,
  payload: any,
  selectedShelves: Shelf[],
) {
  const { color } = payload;
  let drawerSelected = yield select(makeSelectDrawersSelected());
  const isHandlePanelVisible = yield select(
    makeSelectIsVisibleColorsPanel(ActivePanel.handles),
  );

  if (isHandlePanelVisible) {
    drawerSelected = yield select(makeSelectHandlesSelection());
  }

  for (const shelf of selectedShelves) {
    if (drawerSelected === 'All' || drawerSelected.includes('Drawers')) {
      for (const drawer of shelf.drawers) {
        shelf.updateDrawer(
          drawer.position as number,
          undefined,
          undefined,
          undefined,
          color,
        );
      }
    } else {
      for (const position of drawerSelected) {
        shelf.updateDrawer(
          position as number,
          undefined,
          undefined,
          undefined,
          color,
        );
      }
    }
  }
  return {
    ignore: false,
    postSuccess: null,
  };
}

export function* compositionSaga(
  cabinetOrigin: Cabinet,
  cabinetNext: Cabinet,
  payload: any,
  selectedShelves: Shelf[],
) {
  const { composition } = payload;

  const filteredSelectedShelves = selectedShelves.filter(
    item => item.height < MINIMUM_LARGE_DOORS_HEIGHT,
  );
  if (filteredSelectedShelves.length > 0) {
    selectedShelves = filteredSelectedShelves;
  }

  selectedShelves.map(shelf => (shelf.composition = composition));
  return {
    ignore: false,
    postSuccess: null,
  };
}

export function* updatingColorSaga(
  cabinetOrigin: Cabinet,
  cabinetNext: Cabinet,
  payload: any,
  selectedShelves: Shelf[],
) {
  const { material, color } = payload;
  const shelfSelectionProfile: ShelfSelection = yield select(
    makeSelectShelfSelectionProfile(),
  );
  for (const shelf of selectedShelves) {
    for (const position of shelfSelectionProfile.doors) {
      shelf.updateDoor(position, null, null, material, null, color);
    }
    for (const position of shelfSelectionProfile.panes) {
      for (const rack of shelf.racks) {
        if (!shelfSelectionProfile.rackLevelUnSelected.includes(rack.level)) {
          rack.updatePane(position, null, null, material, color);
          if (position === Position.Left || position === Position.Right) {
            shelf.updateSupportIS();
          }
        }
      }
    }
  }
  return {
    ignore: false,
    postSuccess: null,
  };
}

export function* addPlateSaga(
  cabinetOrigin: Cabinet,
  cabinetNext: Cabinet,
  payload: any,
  selectedShelves: Shelf[],
) {
  const {
    glass,
    material,
    color,
    updateShelvesPlates,
    updateTopPlate,
  } = payload;

  if (updateTopPlate) {
    if (cabinetNext.top.plate) {
      cabinetNext.top.updatePlate(null, null, material, color, glass);
    } else {
      //TODO : create addPlate to update and add at the same time
      cabinetNext.top.plate = Plate.standardPlate(
        cabinetNext.depth,
        cabinetNext.width,
        Position.Front,
      );
      cabinetNext.top.updatePlate(null, null, material, color, glass);
    }
  }
  if (updateShelvesPlates) {
    for (const shelf of selectedShelves) {
      const firstRack = shelf.rack(0);
      if (firstRack) {
        if (!firstRack.plate) {
          firstRack.plate = Plate.standardPlate(
            shelf.depth,
            shelf.width,
            Position.Front,
          );
        }

        firstRack.updatePlate(null, null, material, color, glass);
      }
    }
  }

  return {
    ignore: false,
    postSuccess: null,
  };
}

export function* removePlateSaga(
  cabinetOrigin: Cabinet,
  cabinetNext: Cabinet,
  payload: any,
  selectedShelves: Shelf[],
) {
  const { top } = payload;
  if (top) {
    cabinetNext.top.plate = undefined;
  } else {
    for (const shelf of selectedShelves) {
      const firstRack = shelf.rack(0);
      if (firstRack) {
        firstRack.plate = undefined;
        firstRack.glassPlate = undefined;
      }
    }
  }
  return {
    ignore: false,
    postSuccess: null,
  };
}

export function* changeCrossPieceSaga(
  cabinetOrigin: Cabinet,
  cabinetNext: Cabinet,
  payload: any,
  selectedShelves: Shelf[],
) {
  const { positionCP, inferior, crossPieceType, reinforce, drill } = payload;

  if (selectedShelves.length !== 1) {
    throw new Error(
      'Changing the cross piece should only be apply on one shelf at the time',
    );
  }

  let rack: Rack;
  if (!inferior) {
    // TOP
    rack = cabinetNext.top;
  } else {
    rack = selectedShelves[0].rack(0);
  }

  const crossPiece = rack.getCrossPiece(positionCP);
  if (crossPiece) {
    rack.updateCrossPiece(
      positionCP,
      undefined,
      undefined,
      crossPieceType,
      reinforce,
      false,
      drill,
    );
  } else {
    let width = cabinetNext.width;
    if (positionCP === Position.Left || positionCP === Position.Right) {
      width = cabinetNext.depth;
    }
    rack.addCrossPiece(positionCP, width, undefined, crossPieceType, reinforce);
  }

  return {
    ignore: false,
    postSuccess: null,
  };
}

export function* removeCrossPieceSaga(
  cabinetOrigin: Cabinet,
  cabinetNext: Cabinet,
  payload: any,
  selectedShelves: Shelf[],
) {
  const { positionCP, inferior } = payload;

  if (selectedShelves.length !== 1) {
    throw new Error(
      'Changing the cross piece should only be apply on one shelf at the time',
    );
  }

  let rack: Rack;
  if (!inferior) {
    // TOP
    rack = cabinetNext.top;
  } else {
    rack = selectedShelves[0].rack(0);
  }

  rack.removeCrossPiece(positionCP);

  return {
    ignore: false,
    postSuccess: null,
  };
}

export function* removeRodsSaga(
  cabinetOrigin: Cabinet,
  cabinetNext: Cabinet,
  payload: any,
  selectedShelves: Shelf[],
) {
  for (const shelf of selectedShelves) {
    if (shelf.height >= MINIMUM_LARGE_DOORS_HEIGHT) {
      if (shelf.rod) {
        shelf.removeRod(true);
      }
    }
  }

  return {
    ignore: false,
    postSuccess: null,
  };
}

export function* addRodsSaga(
  cabinetOrigin: Cabinet,
  cabinetNext: Cabinet,
  payload: any,
  selectedShelves: Shelf[],
) {
  for (const shelf of selectedShelves) {
    if (shelf.height >= MINIMUM_LARGE_DOORS_HEIGHT) {
      if (!shelf.rod) {
        shelf.addRod();
      }
    }
  }

  return {
    ignore: false,
    postSuccess: null,
  };
}
